/**
 * The on-device input log of the `?inkdebug=1` panel: a ring of the last INPUT_LOG_CAP raw events the
 * window saw (pointer, focus, Scribble's beforeinput, whether the canvas cancelled a touchmove) plus
 * app entries: a `route` row from each legacy owner that acts on a contact (`logRoute`: MarkupLayer's
 * claim with its page point, the camera, select, links, badges, element move, inspect, scale mode), and the
 * OUTCOMES (core/ink/outcomeLog.ts): every markup write (`edit`), what a select tap hit (`hit`), where a snap
 * moved a committed point (`snap`), the tool (`tool`), the camera (`cam` rows, and `cam` on every pointerdown).
 * It answers what only the iPad can: does the Pencil reuse its pointerId, does focus leave the editor, is there a
 * visualViewport offset after the keyboard, where did the tip end up. "Copy log" hands over `{header, rows}`.
 *
 * Window CAPTURE + PASSIVE listeners only: it sees every contact (even one a handler stops) and can
 * never change one. Off by default -- nothing is installed and every writer returns at its first statement.
 *
 * PRIVACY (analytics PR #497, "file names never leave"): no document or file name, no markup text -- a text is
 * logged as its length. The header carries the device and the camera, nothing about the document but its page
 * count.
 */
import { inkDebugEnabled, setInkInputLog, setInkLogCopySource } from './inkDebugOverlay';

export const INPUT_LOG_CAP = 2000;
/** pointermove is sampled per contact: its first move, then every Nth -- and always its last before the up. */
export const MOVE_SAMPLE_EVERY = 10;

export type InputLogEntry = { t: number; k: string } & Record<string, unknown>;

/**
 * Who writes a `route` row (spec--pdfmarkuptool-input-primitives P4 `onRoute`). `router`: the P4 router (S8b), with
 * the row's real owner (`camera | tool | control | inert`) and its section 5 row number. The rest are the LEGACY owners
 * that still act on a contact themselves (`owner: 'legacy'`): `markup-layer` and `camera` only under the kill switch
 * (`pmt.input.router=legacy`); select, links, badges, element move, inspect and scale mode until S9.
 */
export type RouteSite =
    | 'router'
    | 'markup-layer'
    | 'camera'
    | 'select'
    | 'view-link'
    | 'request-badge'
    | 'element-move'
    | 'bim-inspect'
    | 'scale-mode';

/** The viewer's live camera: screen px per page unit (`zoom`), the live page, the pan (CSS px), the page count. */
export interface InputLogCamera {
    zoom: number;
    page: number;
    panX: number;
    panY: number;
    pages: number;
}

/** Facts the header carries that live in the viewer (ViewerToolContext pushes them while the log is on). */
export interface InputLogContext {
    stylusMode?: string;
    penSeen?: boolean;
    inputMode?: string | null;
}

/** What Copy log hands over. */
export interface InputLogCopy {
    header: Record<string, unknown>;
    rows: InputLogEntry[];
}

let enabled = false;
let ring: InputLogEntry[] = [];
/** The last pointerdown the window saw: the contact a row from a handler without a pointer (a click) is about. */
let lastDown: { pointerId: number; pointerType: string } | null = null;
let camera: InputLogCamera | null = null;
let context: InputLogContext = {};
let inputModeSource: (() => string | null) | null = null;
/** What last moved the camera (pan / pinch / wheel / fly-to): the `why` of the next settled `cam` row. */
let cameraWhy: string | null = null;
/** How long the camera holds still before its `cam` row (ViewerNavigationContext's settle timer, and the release flush). */
export const CAMERA_SETTLE_MS = 150;
/** Touches down now (window capture), and the ones a camera gesture owns (from its pan / pinch route rows). */
const touchesDown = new Set<number>();
const cameraContacts = new Set<number>();
/** A `cam` row the camera owed while its gesture was still down: written once the gesture's last contact lifts. */
let settleHeld = false;

const now = () => Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) * 10) / 10;
const r1 = (n: number) => Math.round(n * 10) / 10;

/** Record one entry (no-op unless the log is on). */
export function logInput(entry: { k: string } & Record<string, unknown>): void {
    if (!enabled) return;
    ring.push({ t: now(), ...entry });
    if (ring.length > INPUT_LOG_CAP) ring.splice(0, ring.length - INPUT_LOG_CAP);
    setInkInputLog(ring);
}

/**
 * One trace row from an owner that acted on a contact: who (`site`), which contact, and why (`reason`:
 * claim, pan-start, palm-inert, ...). `owner` is `legacy` unless `detail.owner` says otherwise (the router's rows). The first statement is the flag check, so a call costs one boolean when
 * the log is off; pass `detail` only behind `inputLogEnabled()` (an object literal is built at the call site).
 * `e` null (or a MouseEvent with no pointerId): the contact is the last pointerdown the window saw.
 */
export function logRoute(
    site: RouteSite,
    e: { pointerId?: number; pointerType?: string } | null,
    reason: string,
    detail?: Record<string, unknown>,
): void {
    if (!enabled) return;
    if ((site === 'camera' || site === 'router') && (reason === 'pan-start' || reason === 'pinch-start' || reason === 'control-handoff-pan')) {
        cameraWhy = reason === 'pinch-start' ? 'pinch' : 'pan';
        // The gesture's contacts: a pinch owns every finger down (its first one may have been only tracked).
        if (e && typeof e.pointerId === 'number') cameraContacts.add(e.pointerId);
        if (reason === 'pinch-start') for (const id of touchesDown) cameraContacts.add(id);
    }
    const own = e && typeof e.pointerId === 'number' ? e : null;
    logInput({
        k: 'route',
        owner: 'legacy',
        site,
        pointerId: own ? own.pointerId : lastDown?.pointerId ?? null,
        pointerType: own ? own.pointerType : lastDown?.pointerType ?? null,
        reason,
        focus: activeTag(),
        ...detail,
    });
}

/** A copy of the ring, oldest first. */
export function readInputLog(): InputLogEntry[] {
    return ring.slice();
}

export function inputLogEnabled(): boolean {
    return enabled;
}

/** A point rounded for the log (page or client units). */
export function logPoint(p: { x: number; y: number } | null | undefined): { x: number; y: number } | null {
    return p ? { x: r1(p.x), y: r1(p.y) } : null;
}

// ---- camera ------------------------------------------------------------------------------------------------

/** The viewer's camera changed (ViewerNavigationContext, only while the log is on). */
export function setInputLogCamera(cam: InputLogCamera): void {
    if (!enabled) return;
    camera = cam;
}

/** The camera as a row field: `{zoom, page, panX, panY}` (null before the viewer mounted). */
export function logCamera(): { zoom: number; page: number; panX: number; panY: number } | null {
    if (!enabled || !camera) return null;
    return { zoom: Math.round(camera.zoom * 10000) / 10000, page: camera.page, panX: r1(camera.panX), panY: r1(camera.panY) };
}

/** What is moving the camera now (`wheel`, `fly-to`; a pan / pinch is read from its route row). */
export function noteCameraMove(why: string): void {
    if (!enabled) return;
    cameraWhy = why;
}

/**
 * The camera came to rest: one `cam` row with the new values and what moved it. Not while a pan / pinch is still
 * down (a gesture held still is not at rest: its fingers may move on) -- the row waits for the gesture's last lift.
 */
export function logCameraSettled(): void {
    if (!enabled) return;
    if (cameraContacts.size > 0) {
        settleHeld = true;
        return;
    }
    settleHeld = false;
    logInput({ k: 'cam', why: cameraWhy ?? 'other', ...logCamera() });
    cameraWhy = null;
}

/** A contact ended: the last of a camera gesture's writes the row it held -- unless the release moves the camera. */
function cameraContactEnded(pointerId: number): void {
    touchesDown.delete(pointerId);
    if (!cameraContacts.delete(pointerId) || cameraContacts.size > 0 || !settleHeld) return;
    // The release may commit a camera (the exact pinch zoom, a fling): that settle writes the row and clears the hold.
    setTimeout(() => { if (settleHeld && cameraContacts.size === 0) logCameraSettled(); }, CAMERA_SETTLE_MS);
}

// ---- header ------------------------------------------------------------------------------------------------

/** Viewer facts for the header (stylus mode, the pen latch). */
export function setInputLogContext(patch: InputLogContext): void {
    if (!enabled) return;
    context = { ...context, ...patch };
}

function buildConst(read: () => string): string | null {
    try {
        const v = read();
        return typeof v === 'string' && v ? v : null;
    } catch {
        return null; // not defined (tests, SSR)
    }
}

/**
 * The Copy log header: the build, the device and the camera. NO document or file name and no markup text
 * (inputLog.test.ts pins the key set).
 */
export function inputLogHeader(): Record<string, unknown> {
    const w = typeof window !== 'undefined' ? window : null;
    const cam = camera;
    return {
        appVersion: buildConst(() => __APP_VERSION__) ?? 'dev',
        build: buildConst(() => __BUILD_COMMIT__),
        at: new Date().toISOString(),
        viewport: w ? { w: w.innerWidth, h: w.innerHeight } : null,
        devicePixelRatio: w ? w.devicePixelRatio : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        stylusMode: context.stylusMode ?? null,
        penSeen: context.penSeen ?? null,
        inputMode: inputModeSource?.() ?? context.inputMode ?? null,
        pages: cam?.pages ?? null,
        page: cam?.page ?? null,
        zoom: cam ? Math.round(cam.zoom * 10000) / 10000 : null,
        rowCap: INPUT_LOG_CAP,
    };
}

/** What Copy log hands over: `{header, rows}`. */
export function readInputLogCopy(): InputLogCopy {
    return { header: inputLogHeader(), rows: readInputLog() };
}

function describeTarget(target: EventTarget | null): string {
    const el = target as Element | null;
    if (!el || typeof el.getAttribute !== 'function') return target === window ? 'window' : target === document ? 'document' : '-';
    const testid = el.getAttribute('data-testid');
    const cls = (el.getAttribute('class') ?? '').trim().split(/\s+/).slice(0, 2).join('.');
    return `${el.tagName.toLowerCase()}${testid ? `[${testid}]` : ''}${cls ? `.${cls}` : ''}`;
}

function activeTag(): string {
    return typeof document !== 'undefined' ? describeTarget(document.activeElement) : '-';
}

/**
 * Install the window listeners when `?inkdebug` is on (or `force`). Returns the uninstall (tests).
 * Safe to call more than once: a second call while installed is a no-op. `sources.inputMode`: the latched input
 * mode for the header (analytics/inputMode.ts passes its getter; this module cannot import it -- a cycle).
 */
export function installInputLog(force?: boolean, sources: { inputMode?: () => string | null } = {}): () => void {
    if (enabled || typeof window === 'undefined') return () => {};
    if (!(force ?? inkDebugEnabled())) return () => {};
    enabled = true;
    inputModeSource = sources.inputMode ?? null;
    const moves = new Map<number, number>();
    // The newest move a sample skipped, per contact: written before its up / cancel, so the log always holds where
    // the contact was last seen moving.
    const unlogged = new Map<number, { k: string } & Record<string, unknown>>();
    const touchMoves = new Map<number, number>();
    const opts = { capture: true, passive: true } as const;

    const onPointer = (e: PointerEvent) => {
        const entry: { k: string } & Record<string, unknown> = {
            k: e.type,
            pointerType: e.pointerType,
            pointerId: e.pointerId,
            isPrimary: e.isPrimary,
            buttons: e.buttons,
            pressure: r1(e.pressure * 100) / 100,
            x: r1(e.clientX),
            y: r1(e.clientY),
            target: describeTarget(e.target),
        };
        if (e.type === 'pointermove') {
            const n = (moves.get(e.pointerId) ?? 0) + 1;
            moves.set(e.pointerId, n);
            entry.n = n;
            if (n !== 1 && n % MOVE_SAMPLE_EVERY !== 0) {
                // a hover (no button) has no up to flush before: only a contact's skipped moves are kept
                if (e.buttons !== 0) unlogged.set(e.pointerId, entry);
                return;
            }
            unlogged.delete(e.pointerId);
        } else if (e.type !== 'pointerdown') {
            const last = unlogged.get(e.pointerId);
            if (last) logInput({ ...last, last: true });
            unlogged.delete(e.pointerId);
            moves.delete(e.pointerId);
        } else {
            moves.set(e.pointerId, 0);
            unlogged.delete(e.pointerId);
            lastDown = { pointerId: e.pointerId, pointerType: e.pointerType };
            if (e.pointerType === 'touch') touchesDown.add(e.pointerId);
        }
        if (e.type === 'pointerdown') {
            const vv = window.visualViewport;
            entry.vv = vv ? { top: r1(vv.offsetTop), left: r1(vv.offsetLeft), scale: r1(vv.scale * 100) / 100 } : null;
            entry.cam = logCamera();
        }
        logInput(entry);
        if (e.type === 'pointerup' || e.type === 'pointercancel') cameraContactEnded(e.pointerId);
    };
    const onFocus = (e: FocusEvent) => logInput({ k: e.type, target: describeTarget(e.target), active: activeTag() });
    const onBeforeInput = (e: Event) => logInput({ k: 'beforeinput', inputType: (e as InputEvent).inputType, target: describeTarget(e.target) });
    const onTouch = (e: TouchEvent) => {
        if (e.type === 'touchmove') {
            const id = e.changedTouches[0]?.identifier ?? -1;
            const n = (touchMoves.get(id) ?? 0) + 1;
            touchMoves.set(id, n);
            if (n !== 1 && n % MOVE_SAMPLE_EVERY !== 0) return;
            // defaultPrevented is only known once every listener ran: read it after the dispatch.
            const target = describeTarget(e.target);
            setTimeout(() => logInput({ k: 'touchmove', n, defaultPrevented: e.defaultPrevented, target }), 0);
        } else {
            for (const t of Array.from(e.changedTouches)) touchMoves.delete(t.identifier);
        }
    };

    const pointerTypes = ['pointerdown', 'pointermove', 'pointerup', 'pointercancel'] as const;
    for (const type of pointerTypes) window.addEventListener(type, onPointer, opts);
    window.addEventListener('focusin', onFocus, opts);
    window.addEventListener('focusout', onFocus, opts);
    window.addEventListener('beforeinput', onBeforeInput, opts);
    window.addEventListener('touchmove', onTouch, opts);
    window.addEventListener('touchend', onTouch, opts);
    window.addEventListener('touchcancel', onTouch, opts);
    setInkLogCopySource(readInputLogCopy);
    setInkInputLog(ring, true); // the panel and its Copy button show from the start
    // The e2e harness's trace() reader (e2e/helpers/pencil.ts): the same ring Copy log hands over. Flag on only.
    const w = window as unknown as { __pmtInputLog?: () => InputLogEntry[]; __pmtInputLogCopy?: () => InputLogCopy };
    w.__pmtInputLog = readInputLog;
    w.__pmtInputLogCopy = readInputLogCopy;

    return () => {
        delete w.__pmtInputLog;
        delete w.__pmtInputLogCopy;
        lastDown = null;
        camera = null;
        context = {};
        cameraWhy = null;
        touchesDown.clear();
        cameraContacts.clear();
        settleHeld = false;
        inputModeSource = null;
        setInkLogCopySource(null);
        for (const type of pointerTypes) window.removeEventListener(type, onPointer, opts);
        window.removeEventListener('focusin', onFocus, opts);
        window.removeEventListener('focusout', onFocus, opts);
        window.removeEventListener('beforeinput', onBeforeInput, opts);
        window.removeEventListener('touchmove', onTouch, opts);
        window.removeEventListener('touchend', onTouch, opts);
        window.removeEventListener('touchcancel', onTouch, opts);
        enabled = false;
        ring = [];
    };
}
